import { m } from 'framer-motion';
import { Box, Divider, IconButton, MenuItem, Typography } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { useResponsive } from 'src/hooks/use-responsive';
import { useBoolean } from 'src/hooks/use-boolean';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import Iconify from 'src/components/iconify';
import { varHover } from 'src/components/animate';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { Colors } from 'src/theme/palette';
import { useRentalsCustomerAuth } from 'src/auth/rentals-customer/rentals-customer-auth';
import { ICONS } from '../dashboard/config-navigation';
import Header from '../dashboard/header';
import NavVertical from '../dashboard/nav-vertical';
import Main from '../dashboard/main';

// the customer counterpart of the org header's AccountPopover, same look
function CustomerAccountPopover() {
  const { session, logout } = useRentalsCustomerAuth();
  const router = useRouter();
  const popover = usePopover();

  const customer = session?.customer;

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={popover.open ? 'inherit' : 'default'}
        onClick={popover.onOpen}
        sx={{
          ...(popover.open && {
            bgcolor: (theme) => theme.palette.action.selected,
          }),
        }}
      >
        <Iconify width={24} icon="solar:user-rounded-bold-duotone" color={Colors.greyText} />
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 200, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {customer && `${customer.firstName} ${customer.lastName}`}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {customer?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={() => {
            popover.onClose();
            logout();
            router.push(paths.rentalsPortal.login);
          }}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Logout
        </MenuItem>
      </CustomPopover>
    </>
  );
}

export default function RentalsCustomerDashboardLayout() {
  const { session } = useRentalsCustomerAuth();

  const lgUp = useResponsive('up', 'lg');
  const nav = useBoolean(lgUp);

  const settings = session?.organizationSettings;

  const navData = [
    {
      subheader: 'Rentals',
      items: [
        { title: 'Map', path: paths.rentalsPortal.map, icon: ICONS.map },
        { title: 'My Rentals', path: paths.rentalsPortal.trailers, icon: ICONS.equipment },
        { title: 'Contact', path: paths.rentalsPortal.contact, icon: ICONS.mail },
      ],
    },
  ];

  const accountArea = <CustomerAccountPopover />;

  return (
    <>
      <Box displayPrint="none">
        <Header openNav={nav.value} onOpenNav={nav.onToggle} accountArea={accountArea} />
      </Box>

      <Box
        sx={{
          minHeight: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box displayPrint="none" sx={{ background: Colors.topbarSidebar, zIndex: 1, boxShadow: '-1px -3px 6px #ddd' }}>
          <NavVertical
            openNav={nav.value}
            onCloseNav={nav.onFalse}
            customLogoUrl={settings?.companyLogoUrl || undefined}
            navDataOverride={navData}
            contactEmail={settings?.contactEmail || undefined}
            contactPhone={settings?.contactPhone || undefined}
          />
        </Box>

        <Main openNav={nav.value}>
          <Outlet />
        </Main>
      </Box>
    </>
  );
}
